// src/pages/payment-link.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FaLink, FaTrashAlt, FaEdit } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import PaymentLinkProductForm from '../components/payment-link-product-form';
import { getPaymentLinks, savePaymentLink, deletePaymentLink, getAllSocieties } from '../services/api.service';
import Helper from '../services/helper.service';
import Loading from '../components/loading';
import { useConfirm } from '../components/confirm-dialog';

const totalOf = (pl) =>
    (pl.products || []).reduce(
        (acc, p) => acc + (Number(p.priceTTC) || 0) * (Number(p.quantity) || 1),
        0
    );

const PaymentLinkPage = () => {
    const [allPaymentLinks, setAllPaymentLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPaymentLink, setCurrentPaymentLink] = useState(null);
    const [rightSideIsOpen, setRightSideIsOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editingProductIndex, setEditingProductIndex] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [societies, setSocieties] = useState([]);
    const confirm = useConfirm();

    const [searchParams] = useSearchParams();
    const focusParam = searchParams.get('focus');
    const txParam = searchParams.get('tx');
    const amountParam = searchParams.get('amount');
    const txDateParam = searchParams.get('txDate');
    const amountFromTx = Number.isFinite(Number(amountParam)) ? Number(amountParam) : null;
    const txTimestamp = useMemo(
        () => (txDateParam ? Date.parse(txDateParam) : null),
        [txDateParam]
    );

    useEffect(() => {
        const fetchLinks = async () => {
            setLoading(true);
            try {
                const [links, societiesList] = await Promise.all([
                    getPaymentLinks().catch(() => {
                        Helper.toast('Erreur chargement des liens de paiement', 'error');
                        return [];
                    }),
                    getAllSocieties().catch(() => {
                        Helper.toast('Erreur chargement des sociétés', 'error');
                        return [];
                    }),
                ]);
                setAllPaymentLinks(links || []);
                setSocieties(societiesList || []);
            } catch (err) {
                console.error('Erreur de chargement :', err);
            } finally {
                setLoading(false);
            }
        };
        fetchLinks();
    }, []);

    // --- Auto-open & scroll ---------------------------------------------------
    useEffect(() => {
        if (loading || !allPaymentLinks.length) return;

        let target = null;

        if (focusParam) {
            target =
                allPaymentLinks.find((l) => l._id === focusParam || l.token === focusParam) || null;
        }

        if (!target && txParam) {
            target =
                allPaymentLinks.find(
                    (l) => l.transactionId === txParam || l.lastTransactionId === txParam
                ) || null;
        }

        if (!target && amountFromTx != null) {
            const within = allPaymentLinks.filter((l) => {
                const amountMatches = Math.abs(totalOf(l) - amountFromTx) < 0.01;
                const closeInTime =
                    txTimestamp != null && l.createdAt
                        ? Math.abs(new Date(l.createdAt).getTime() - txTimestamp) <= 1000 * 60 * 60 * 48
                        : true;
                return amountMatches && closeInTime;
            });
            target = within[0] || null;
        }

        if (target) {
            openRightSide(target);
            setTimeout(() => {
                const el = document.getElementById(`pl-${target._id}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('ring-2', 'ring-blue-400', 'rounded');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'rounded'), 3000);
                }
            }, 80);
        }
    }, [loading, allPaymentLinks, focusParam, txParam, amountFromTx, txTimestamp]);

    const selectedSociety = useMemo(
        () => societies.find((s) => s._id === (currentPaymentLink?.societyId || '')),
        [societies, currentPaymentLink?.societyId]
    );

    const linkedBankName =
        (selectedSociety?.bankId && typeof selectedSociety.bankId === 'object'
            ? (selectedSociety.bankId.name?.fr || selectedSociety.bankId.name)
            : '') || selectedSociety?.bank?.name || '';

    const copyLink = (token) => {
        navigator.clipboard.writeText(`https://services.fiskagroup.com/payment-link/${token}`);
        Helper.toast('Lien copié', 'success');
    };

    const applyStatusToLink = (pp, status) => {
        if (status === 'payé') return { ...pp, isPaid: true, isExpired: false };
        if (status === 'expiré') return { ...pp, isPaid: false, isExpired: true };
        return { ...pp, isPaid: false, isExpired: false }; // actif
    };

    const updateLinkStatus = async (pp, status) => {
        const updated = applyStatusToLink(pp, status);
        setAllPaymentLinks((prev) => prev.map((x) => (x._id === pp._id ? updated : x)));
        if (currentPaymentLink?._id === pp._id) setCurrentPaymentLink(updated);

        try {
            const saved = await savePaymentLink(updated, 'Statut mis à jour');
            setAllPaymentLinks((prev) => prev.map((x) => (x._id === saved._id ? saved : x)));
            if (currentPaymentLink?._id === pp._id) setCurrentPaymentLink(saved);
        } catch {
            Helper.toast('Erreur de mise à jour du statut', 'error');
        }
    };

    const openRightSide = (link = null, createNew = false) => {
        setEditingProduct(null);
        setEditingProductIndex(null);

        if (createNew) {
            setCurrentPaymentLink({ label: '', products: [], canBeUsedOnlyOnce: true, societyId: '', isDebours: false });
        } else {
            setCurrentPaymentLink(link);
        }
        setRightSideIsOpen(true);
    };

    const addOrEditProduct = (product = {}, index = -1) => {
        // When "debours", default label and lock TVA to 0
        const isDebours = !!currentPaymentLink?.isDebours;
        const base = {
            label: isDebours ? 'Frais de débours ' : '',
            quantity: 1,
            priceTTC: 0,
            tax: isDebours ? 0 : 20,
            description: '',
        };
        const merged = {
            ...base,
            ...product,
            // ensure tax stays 0 if debours (even when editing)
            tax: isDebours ? 0 : (product.tax ?? base.tax),
        };
        setEditingProduct(merged);
        setEditingProductIndex(index);
    };

    const saveProduct = (updatedProduct) => {
        if (currentPaymentLink?.isDebours) updatedProduct = { ...updatedProduct, tax: 0 };

        const updatedProducts = [...(currentPaymentLink.products || [])];
        if (editingProductIndex !== null && editingProductIndex > -1) {
            updatedProducts[editingProductIndex] = updatedProduct;
        } else {
            updatedProducts.push(updatedProduct);
        }
        setCurrentPaymentLink({ ...currentPaymentLink, products: updatedProducts });
        setEditingProduct(null);
        setEditingProductIndex(null);
    };

    const deleteProduct = (index) => {
        const updated = [...(currentPaymentLink.products || [])];
        updated.splice(index, 1);
        setCurrentPaymentLink({ ...currentPaymentLink, products: updated });
    };

    const savePublicPayment = async () => {
        try {
            setLoading(true);
            const saved = await savePaymentLink(currentPaymentLink, 'Lien de paiement enregistré');

            setAllPaymentLinks((prev) => {
                const updated = [...prev];
                const idx = updated.findIndex((l) => l._id === saved._id);
                if (idx !== -1) updated[idx] = saved;
                else updated.unshift(saved);
                return updated;
            });

            setRightSideIsOpen(false);
            setCurrentPaymentLink(null);
        } catch (err) {
            Helper.toast("Erreur lors de l'enregistrement du lien", 'error');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const deleteLink = async (link) => {
        if (!link._id) return;

        const ok = await confirm({
            title: 'Attention !',
            message: <>Supprimer le lien <strong>{link.label}</strong> ?</>,
            confirmText: 'Oui',
            cancelText: 'Non',
            tone: 'warning',
        });

        if (!ok) return Helper.toast('Action annulée', 'info');

        try {
            await deletePaymentLink(link._id);
            setAllPaymentLinks((prev) => prev.filter((p) => p._id !== link._id));
            Helper.toast('Lien supprimé', 'success');

            if (currentPaymentLink?._id === link._id) {
                setRightSideIsOpen(false);
                setCurrentPaymentLink(null);
            }
        } catch (err) {
            Helper.toast('Erreur lors de la suppression', 'error');
            console.error(err);
        }
    };

    const shownLinks = useMemo(() => {
        return allPaymentLinks.filter((pp) => {
            if (!filterStatus) return true;
            if (filterStatus === 'actif') return !pp.isExpired && !pp.isPaid;
            if (filterStatus === 'payé') return pp.isPaid;
            if (filterStatus === 'expiré') return pp.isExpired;
            return true;
        });
    }, [allPaymentLinks, filterStatus]);

    const getSocietyName = useCallback(
        (id) => (societies.find((s) => s._id === id)?.name) || '',
        [societies]
    );

    return (
        <section className="max-w-screen-2xl mx-auto p-6">
            <h1 className="text-2xl font-bold text-gray-800">Liens de paiement</h1>

            <div className="my-6 flex justify-between items-center">
                <div className="mb-4 flex items-center gap-4">
                    <div className="relative w-full sm:w-60">
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className={`appearance-none bg-white w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${filterStatus !== '' ? 'capitalize' : ''
                                }`}
                        >
                            <option value="">Tous les statuts</option>
                            <option value="actif">Statut : Actif</option>
                            <option value="payé">Statut : Payé</option>
                            <option value="expiré">Statut : Expiré</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>

                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded shadow"
                    onClick={() => openRightSide(null, true)}
                >
                    Créer un nouveau lien
                </button>
            </div>

            {loading ? (
                <div className="text-center py-20">
                    <Loading />
                </div>
            ) : (
                <div className="flex gap-4">
                    {/* LEFT */}
                    <div className="w-1/3 bg-white border rounded shadow-sm divide-y h-fit">
                        {shownLinks.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                Aucun lien de paiement {filterStatus ? `(${filterStatus})` : 'disponible'}.
                            </div>
                        ) : (
                            shownLinks.map((pp) => (
                                <div
                                    id={`pl-${pp._id}`}
                                    key={pp._id}
                                    className={`p-4 pb-2 cursor-pointer hover:bg-gray-100 ${currentPaymentLink?._id === pp._id ? 'bg-gray-100 font-semibold' : ''
                                        }`}
                                    onClick={() => openRightSide(pp)}
                                >
                                    <div className="flex items-start gap-3 justify-between">
                                        <div className="flex-1">
                                            <div className="text-lg font-semibold text-gray-800">{pp.label}</div>

                                            {/* STATUS */}
                                            <div className="mt-2">
                                                {pp.isPaid ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm font-medium text-gray-700">
                                                        ✅ Payé
                                                    </span>
                                                ) : (
                                                    <div className="relative inline-flex items-center">
                                                        <select
                                                            className="appearance-none rounded-full border border-gray-300 bg-white pl-3 pr-7 py-1 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 capitalize"
                                                            value={pp.isExpired ? 'expiré' : 'actif'}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                updateLinkStatus(pp, e.target.value); // 'actif' | 'expiré'
                                                            }}
                                                            title="Modifier le statut"
                                                        >
                                                            <option value="actif">🟢 Actif</option>
                                                            <option value="expiré">⛔ Expiré</option>
                                                        </select>
                                                        <svg
                                                            className="pointer-events-none absolute right-2 h-3 w-3 text-gray-500"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            aria-hidden="true"
                                                        >
                                                            <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Linked society (if any) */}
                                            {pp.societyId && (
                                                <div className="text-xs text-gray-600 mt-2">
                                                    Société : <b>{getSocietyName(pp.societyId) || '—'}</b>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteLink(pp);
                                            }}
                                            className="text-red-600 hover:text-red-800"
                                            title="Supprimer"
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </div>

                                    {/* Résumé des produits */}
                                    <div className="text-xs text-gray-600 mt-3">
                                        {pp.products?.length || 0} produit{pp.products?.length > 1 ? 's' : ''} – Total :{' '}
                                        {totalOf(pp).toFixed(2)} €
                                    </div>

                                    <div
                                        className="text-sm text-blue-500 cursor-pointer mt-2 flex items-center gap-1 hover:underline"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            copyLink(pp.token);
                                        }}
                                    >
                                        <FaLink className="inline" /> Copier le lien
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* RIGHT */}
                    {rightSideIsOpen && currentPaymentLink && (
                        <div className="w-2/3 bg-white border rounded shadow-sm p-6 space-y-6">
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                placeholder="Label"
                                value={currentPaymentLink.label}
                                onChange={(e) =>
                                    setCurrentPaymentLink({ ...currentPaymentLink, label: e.target.value })
                                }
                            />

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={currentPaymentLink.canBeUsedOnlyOnce}
                                    onChange={(e) =>
                                        setCurrentPaymentLink({
                                            ...currentPaymentLink,
                                            canBeUsedOnlyOnce: e.target.checked,
                                        })
                                    }
                                />
                                Utilisable une seule fois
                            </label>

                            <input
                                type="email"
                                className="w-full border rounded px-3 py-2"
                                placeholder="Email de notification (facultatif)"
                                value={currentPaymentLink.notificationEmail || ''}
                                onChange={(e) =>
                                    setCurrentPaymentLink({
                                        ...currentPaymentLink,
                                        notificationEmail: e.target.value,
                                    })
                                }
                            />

                            {/* Société associée */}
                            <div>
                                <label className="block font-semibold mb-1">Société associée pour le paiement</label>
                                <div className="relative w-full">
                                    <select
                                        value={currentPaymentLink.societyId || ''}
                                        onChange={(e) =>
                                            setCurrentPaymentLink({ ...currentPaymentLink, societyId: e.target.value })
                                        }
                                        className="appearance-none bg-white w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Aucune société sélectionnée</option>
                                        {societies.map((soc) => (
                                            <option key={soc._id} value={soc._id}>
                                                {soc.name}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                                        <svg
                                            className="h-4 w-4"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>

                                {/* bank name under the select */}
                                {currentPaymentLink.societyId && (
                                    <p className="mt-2 text-sm text-gray-600">
                                        Banque : <span className="font-medium text-gray-800">{linkedBankName || '—'}</span>
                                    </p>
                                )}
                            </div>

                            {/* Facture de débours */}
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={!!currentPaymentLink.isDebours}
                                    onChange={(e) => {
                                        const isDebours = e.target.checked;
                                        // if enabling, force all existing product taxes to 0
                                        const products = (currentPaymentLink.products || []).map(p =>
                                            isDebours ? { ...p, tax: 0 } : p
                                        );
                                        setCurrentPaymentLink({
                                            ...currentPaymentLink,
                                            isDebours,
                                            products,
                                        });
                                    }}
                                />
                                Facture de débours (TVA 0%)
                            </label>
                            <p className="text-xs text-gray-600 -mt-3">
                                En mode débours : TVA verrouillée à 0% et, à l’ajout d’un produit, le libellé est prérempli par <i>“Frais de débours …”</i>.
                            </p>

                            {/* PRODUITS */}
                            <div className="pt-4 border-t">
                                {(currentPaymentLink.products || []).map((product, i) => (
                                    <div key={i} className="mb-3">
                                        <div
                                            className="border rounded p-4 bg-gray-50 flex justify-between items-center"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="space-y-1">
                                                <p className="font-semibold text-gray-800">{product.label}</p>
                                                <p className="text-sm text-gray-600">
                                                    Quantité : <b>{product.quantity}</b> x {parseFloat(product.priceTTC).toFixed(2)}€ TTC
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    Total : <b>{(product.quantity * product.priceTTC).toFixed(2)}€</b>
                                                </p>
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        addOrEditProduct(product, i);
                                                    }}
                                                    className="text-primary hover:text-blue-800"
                                                    title="Modifier"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteProduct(i);
                                                    }}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Supprimer"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </div>
                                        </div>

                                        {editingProduct && editingProductIndex === i && (
                                            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                                <PaymentLinkProductForm
                                                    product={editingProduct}
                                                    lockTaxZero={!!currentPaymentLink.isDebours}
                                                    onSave={(updated) => saveProduct(updated)}
                                                    onCancel={() => {
                                                        setEditingProduct(null);
                                                        setEditingProductIndex(null);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {editingProduct && editingProductIndex === -1 && (
                                    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                                        <PaymentLinkProductForm
                                            product={editingProduct}
                                            lockTaxZero={!!currentPaymentLink.isDebours}
                                            onSave={(updated) => saveProduct(updated)}
                                            onCancel={() => {
                                                setEditingProduct(null);
                                                setEditingProductIndex(null);
                                            }}
                                        />
                                    </div>
                                )}

                                {editingProductIndex !== -1 && (
                                    <button
                                        className="mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded"
                                        onClick={() => addOrEditProduct({}, -1)}
                                    >
                                        Ajouter un produit
                                    </button>
                                )}
                            </div>

                            <div className="flex justify-end gap-2">
                                <button
                                    className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
                                    onClick={() => setRightSideIsOpen(false)}
                                >
                                    Annuler
                                </button>
                                <button
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                                    onClick={savePublicPayment}
                                >
                                    Enregistrer le lien de paiement
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default PaymentLinkPage;